import { useState, useRef, useEffect, useCallback } from 'react';
import { Download, X, Palette, Image } from 'lucide-react';

const translations = {
  en: {
    subtitle: "Simple app for generating numbers.",
    from: "FROM",
    to: "TO",
    draw: "Start",
    log: "Log",
    instructions: "Tap, click, hit space or enter for next number...",
    sequenceComplete: "Sequence Complete",
    sequenceMessage: "All numbers in the range have been generated.",
    restart: "Restart Sequence",
    exit: "Exit to Main",
    remaining: "Numbers remaining"
  },
  no: {
    subtitle: "Enkel app for å generere tall.",
    from: "FRA",
    to: "TIL",
    draw: "Trekk",
    log: "Logg",
    instructions: "Bruk enter, mellomrom eller musetast for neste tall...",
    sequenceComplete: "Sekvensen er fullført",
    sequenceMessage: "Alle tall i området er generert.",
    restart: "Start sekvensen på nytt",
    exit: "Gå til hovedsiden",
    remaining: "Gjenværende tall"
  }
};

const RandomNumberGenerator = () => {
  const [language, setLanguage] = useState('no');
  const [minInput, setMinInput] = useState('1');
  const [maxInput, setMaxInput] = useState('100');
  const [min, setMin] = useState(1);
  const [max, setMax] = useState(100);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [isActive, setIsActive] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [backgroundColor, setBackgroundColor] = useState('#242424');
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [availableNumbers, setAvailableNumbers] = useState([]);
  const [showCompletionDialog, setShowCompletionDialog] = useState(false);
  
  const fileInputRef = useRef();
  const colorPickerRef = useRef();

  const iconButtonStyles = "w-12 h-12 flex items-center justify-center bg-black/30 rounded-full text-white hover:bg-black/40 transition-all";
  const firstPageIconStyles = "w-16 h-16 flex items-center justify-center bg-black/30 rounded-full text-white hover:bg-black/40 transition-all";
   
  const generateNumber = useCallback(() => {
    // Only show completion dialog if we've actually used all numbers
    // and not when we're just starting
    if (availableNumbers.length === 0 && result !== null) {
      setShowCompletionDialog(true);
      return;
    }
  
    const randomIndex = Math.floor(Math.random() * availableNumbers.length);
    const selectedNumber = availableNumbers[randomIndex];
    
    setAvailableNumbers(prev => prev.filter((_, index) => index !== randomIndex));
    setResult(selectedNumber);
    setHistory(prev => [...prev, {
      number: selectedNumber,
      timestamp: new Date().toLocaleString(),
      range: `${min}-${max}`
    }]);
  }, [availableNumbers, min, max, result]);
  
  const reset = useCallback(() => {
    setIsActive(false);
    setResult(null);
    setHistory([]);
    setAvailableNumbers([]);
    setShowCompletionDialog(false);
  }, []);
  
  const handleSequenceComplete = useCallback((action) => {
    if (action === 'restart') {
      const numbers = [];
      for (let i = min; i <= max; i++) {
        numbers.push(i);
      }
      
      const randomIndex = Math.floor(Math.random() * numbers.length);
      const selectedNumber = numbers[randomIndex];
      
      setHistory([]);
      setResult(selectedNumber);
      setHistory([{
        number: selectedNumber,
        timestamp: new Date().toLocaleString(),
        range: `${min}-${max}`
      }]);
      setAvailableNumbers(numbers.filter((_, index) => index !== randomIndex));
    } else {
      reset();
      setIsActive(false);
    }
  }, [min, max, reset]);
  
  const handleImageUpload = (event) => {
    event.stopPropagation();
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setBackgroundImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  const downloadCSV = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const csvContent = 'Number,Timestamp,Range\n' + 
      history.map(item => `${item.number},${item.timestamp},${item.range}`).join('\n');
  
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'random_numbers.csv';
    a.click();
    URL.revokeObjectURL(url);
  };
  
  const start = useCallback(() => {
    if (min >= max) {
      alert('Invalid range!');
      return;
    }
    
    // First initialize the sequence
    const numbers = [];
    for (let i = min; i <= max; i++) {
      numbers.push(i);
    }
    setAvailableNumbers(numbers);
    
    // Then set active and generate first number
    setIsActive(true);
    
    // Generate first number from the full set
    const randomIndex = Math.floor(Math.random() * numbers.length);
    const selectedNumber = numbers[randomIndex];
    
    setAvailableNumbers(prev => prev.filter((_, index) => index !== randomIndex));
    setResult(selectedNumber);
    setHistory([{
      number: selectedNumber,
      timestamp: new Date().toLocaleString(),
      range: `${min}-${max}`
    }]);
  }, [min, max]);
  
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (isActive && (e.code === 'Space' || e.code === 'Enter')) {
        generateNumber();
      }
    };
    window.addEventListener('keyup', handleKeyPress);
    return () => window.removeEventListener('keyup', handleKeyPress);
  }, [isActive, generateNumber]);
  
  const CompletionDialog = () => (
    showCompletionDialog && (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
           onClick={() => setShowCompletionDialog(false)} // Close on background click
      >
        <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
          <h2 className="text-xl font-bold mb-4 text-gray-900">
            {translations[language].sequenceComplete}
          </h2>
          <p className="mb-6 text-gray-600">
            {translations[language].sequenceMessage}
          </p>
          <div className="flex space-x-4">
            <button
              onClick={() => {
                setShowCompletionDialog(false);
                setTimeout(() => {
                  handleSequenceComplete('restart');
                }, 0);
              }}
              className="flex-1 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              {translations[language].restart}
            </button>
            <button
              onClick={() => {
                setShowCompletionDialog(false);
                setTimeout(() => {
                  handleSequenceComplete('exit');
                }, 0);
              }}
              className="flex-1 px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
            >
              {translations[language].exit}
            </button>
          </div>
        </div>
      </div>
    )
  );
  
  if (!isActive) {
    return (
      <div 
        className="min-h-screen flex flex-col"
        style={{ 
          backgroundColor,
          backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="flex-1 flex flex-col justify-center p-8 md:p-16 max-w-7xl mx-auto">
          <h1 className="text-4xl md:text-8xl lg:text-[12rem] font-bold text-white mb-6 max-w-[95vw]">
            RANDOM
          </h1>
          
          <p className="text-xl md:text-2xl mb-16 text-gray-300">
            {translations[language].subtitle}
          </p>
  
          <div className="flex flex-col 2xl:flex-row items-start space-y-4 2xl:space-y-0 2xl:space-x-8 mb-12 w-full max-w-[90vw]">
            <div className="relative bg-white w-full">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg">
                {translations[language].from}
              </span>
              <input
                type="number"
                value={minInput}
                onChange={(e) => {
                  setMinInput(e.target.value);
                  setMin(e.target.value === '' ? 0 : parseInt(e.target.value));
                }}
                className="h-16 w-full pl-20 pr-4 text-2xl font-bold 2xl:h-20 2xl:pl-28 2xl:pr-6 2xl:text-4xl"
                style={{ color: backgroundColor }}
              />
            </div>
  
            <div className="relative bg-white w-full">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg">
                {translations[language].to}
              </span>
              <input
                type="number"
                value={maxInput}
                onChange={(e) => {
                  setMaxInput(e.target.value);
                  setMax(e.target.value === '' ? 0 : parseInt(e.target.value));
                }}
                className="h-16 w-full pl-20 pr-4 text-2xl font-bold 2xl:h-20 2xl:pl-28 2xl:pr-6 2xl:text-4xl"
                style={{ color: backgroundColor }}
              />
            </div>
  
            <button
              onClick={() => setLanguage(prev => prev === 'en' ? 'no' : 'en')}
              className="absolute top-4 right-4 px-4 py-2 bg-black/30 text-white rounded-md hover:bg-black/40 transition-all"
            >
              {language === 'en' ? 'NO' : 'EN'}
            </button>
  
            <button
              onClick={start}
              className="h-16 md:h-20 px-8 md:px-20 text-white text-xl md:text-3xl font-bold rounded-md transition-colors bg-black/30 hover:bg-orange-500 w-full max-w-[90vw]"
            >
              {translations[language].draw}
            </button>
          </div>
  
          <div className="flex space-x-6">
            <div className="relative">
              <input
                type="color"
                value={backgroundColor}
                onChange={(e) => setBackgroundColor(e.target.value)}
                className="w-0 h-0 opacity-0 absolute"
                id="colorPicker"
              />
              <label 
                htmlFor="colorPicker"
                className={firstPageIconStyles}
              >
                <Palette size={28} />
              </label>
            </div>
  
            <div className="relative">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageUpload}
                accept="image/*"
                className="hidden"
              />
              <button 
                onClick={() => fileInputRef.current?.click()}
                className={firstPageIconStyles}
              >
                <Image size={28} />
              </button>
            </div>
  
            {backgroundImage && (
              <button
                onClick={() => setBackgroundImage(null)}
                className={firstPageIconStyles}
              >
                <X size={28} />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div 
      className="min-h-screen relative overflow-hidden"
      style={{ 
        backgroundColor,
        backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
      onClick={generateNumber}
    >

      <CompletionDialog />
  
      {result && (
        <div className="absolute inset-0 flex items-center justify-center text-white">
          <div 
            className="font-bold leading-none select-none text-center"
            style={{
              fontSize: `min(calc(80vw / ${String(result).length}), 80vh)`,
              textShadow: '4px 4px 0px rgba(0,0,0,0.3)',
              lineHeight: '1',
              whiteSpace: 'nowrap',
              textRendering: 'optimizeLegibility',
              WebkitFontSmoothing: 'antialiased',
              MozOsxFontSmoothing: 'grayscale',
            }}
          >
            {result}
          </div>
        </div>
      )}
  
      <div className="fixed left-4 top-4">
        <button
          onClick={(e) => {
            e.stopPropagation();
            reset();
          }}
          className={iconButtonStyles}
        >
          <X size={20} />
        </button>
      </div>
  
      <div className="fixed left-0 bottom-0 p-8 flex flex-col items-start z-10">
        <div className="flex gap-3" onClick={e => e.stopPropagation()}>
          <div className="relative">
            <input
              type="color"
              ref={colorPickerRef}
              value={backgroundColor}
              onChange={(e) => {
                e.stopPropagation();
                setBackgroundColor(e.target.value);
              }}
              onClick={e => e.stopPropagation()}
              className="opacity-0 absolute"
              id="colorPicker2"
              style={{
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                bottom: '100%',
                marginBottom: '8px'
              }}
            />
            <label 
              htmlFor="colorPicker2"
              className={iconButtonStyles}
              onClick={e => {
                e.stopPropagation();
                colorPickerRef.current?.click();
              }}
            >
              <Palette size={20} />
            </label>
          </div>
  
          <div className="relative">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageUpload}
              accept="image/*"
              className="hidden"
            />
            <button 
              className={iconButtonStyles}
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current.click();
              }}
            >
              <Image size={20} />
            </button>
          </div>
  
          {backgroundImage && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setBackgroundImage(null);
              }}
              className={iconButtonStyles}
            >
              <X size={20} />
            </button>
          )}
  
          <button
            onClick={(e) => {
              e.stopPropagation();
              downloadCSV(e);
            }}
            className={iconButtonStyles}
          >
            <Download size={20} />
          </button>
        </div>
        <div className="text-white text-sm mt-2 px-3 py-1 bg-black/50 rounded-md">
          {translations[language].instructions}
        </div>
      </div>
  
      <div 
  className={`fixed right-0 top-0 bottom-0 w-1/5 bg-black/30 transform transition-transform ${showLog ? 'translate-x-0' : 'translate-x-full'}`}
  onClick={e => e.stopPropagation()}
>
  <button
    onClick={() => setShowLog(!showLog)}
    className="absolute -left-12 top-1/2 -translate-y-1/2 origin-center -rotate-90 bg-black/30 text-white px-4 py-2 uppercase font-bold"
  >
    {translations[language].log}
  </button>
  <div className="p-4 text-white overflow-y-auto h-full">
    <div className="mb-4 pb-2 border-b border-white/20 text-sm">
      {translations[language].remaining}: {availableNumbers.length}
    </div>
    <div className="space-y-2">
      {history.map((item, index) => (
        <div key={index} className="flex items-center justify-between">
          <span className="text-lg">{item.number}</span>
          <span className="text-xs text-white/50">
            {new Date(item.timestamp).toLocaleTimeString()}
          </span>
        </div>
      ))}
    </div>
  </div>
</div>
</div>
);
};

export default RandomNumberGenerator;